// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.
//
// NC component (NodeControl Design System). Mirrored from the design system;
// adjust the design system, not this AU copy.
//
// DELIBERATE AU-SPECIFIC DEVIATION (not upstreamed, #64): `titleAdornment`
// below. The canonical component has no slot beside the title, and this AU's
// seat chip ("Demo Seat", "Educational Seat") must sit immediately to the
// right of the title. Added by PM decision in #64, matching the same
// deviation in AU0008 (#79), rather than recreating the header locally (NCDS
// Core Rule 2). Additive only: omitted, the header renders exactly as before.
// Candidate for upstreaming together with the chip (AU0001 / AU0008).


import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

export interface NCPageHeaderProps {
  title: string;
  description?: string;
  eyebrow?: string;
  /**
   * DELIBERATE AU-SPECIFIC DEVIATION (not upstreamed, #64): an optional
   * element rendered inline immediately after the title, wrapping with it on
   * narrow widths. It sits beside the `<h1>`, not inside it, so it never
   * becomes part of the heading's accessible name.
   */
  titleAdornment?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

export function NCPageHeader({
  title,
  description,
  eyebrow,
  titleAdornment,
  actions,
  className,
}: NCPageHeaderProps) {
  return (
    <header
      className={cn(
        "flex flex-col gap-nc-3 border-b border-border pb-nc-3 sm:flex-row sm:items-start sm:justify-between",
        className,
      )}
    >
      <div className="flex flex-col gap-nc-1">
        {eyebrow ? (
          <p className="text-nc-1 font-nc-2 leading-nc-1 uppercase tracking-wide text-muted-foreground">
            {eyebrow}
          </p>
        ) : null}

        {titleAdornment ? (
          <div className="flex flex-wrap items-center gap-x-nc-3 gap-y-nc-1">
            <h1 className="text-nc-4 font-nc-2 leading-nc-1 tracking-tight text-foreground">{title}</h1>
            {titleAdornment}
          </div>
        ) : (
          <h1 className="text-nc-4 font-nc-2 leading-nc-1 tracking-tight text-foreground">{title}</h1>
        )}

        {description ? (
          <p className="text-nc-2 font-nc-1 leading-nc-2 text-muted-foreground">{description}</p>
        ) : null}
      </div>

      {actions ? <div className="flex items-center gap-nc-2">{actions}</div> : null}
    </header>
  );
}
